import { useState, useEffect, useMemo } from "react";
import { useUserSettings } from "../hooks/useUserSettings";
import type UserSetting from "../models/UserSetting";
import { UserSettingsSidebar } from "../components/user_settings_sidebar";
import { UserSettingsTable } from "../components/user_settings_table";
import GlassContainer from "@/shared/components/glasscontainer";
import McpConfigPanel from "@/features/mcp/components/McpConfigPanel";
import UserAccountPanel from "../components/UserAccountPanel";

const USER_TAB = "Usuario";
const MCP_TAB = "MCP";

function getDomain(key: string): string {
  const idx = key.indexOf("_");
  return idx === -1 ? key : key.slice(0, idx);
}

export default function UserSettingsPage() {
  const [settings, setSettings] = useState<UserSetting[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const { loading, error, getKeys, get, set } = useUserSettings();

  useEffect(() => {
    let ignore = false;
    const fetchSettings = async () => {
      const keys = await getKeys();
      if (!ignore && keys) setSettings(keys);
    };
    fetchSettings();
    return () => { ignore = true; };
  }, [getKeys]);

  const domains = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const s of settings) {
      const d = getDomain(s.key);
      if (!seen.has(d)) {
        seen.add(d);
        result.push(d);
      }
    }
    return result;
  }, [settings]);

  const tabs = useMemo(() => [USER_TAB, MCP_TAB, ...domains], [domains]);
  const isUserTab = activeTab === 0;
  const isMcpTab = activeTab === 1;

  const filteredSettings = useMemo(() => {
    if (isUserTab || isMcpTab) return [];
    const domain = domains[activeTab - 2];
    return settings.filter((s) => getDomain(s.key) === domain);
  }, [settings, domains, activeTab, isUserTab, isMcpTab]);

  return (
    <div className="relative flex flex-1 overflow-hidden h-full bg-[#f0f0f3] text-foreground font-sans">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] size-[60%] rounded-full bg-accent/15 blur-[120px] animate-pulse" />
        <div className="absolute bottom-[10%] right-[-5%] size-[50%] rounded-full bg-[#5e5e62]/10 blur-[100px]" />
        <div className="absolute top-[40%] left-[20%] size-[30%] rounded-full bg-accent/10 blur-[140px]" />
        <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(#000_1px,transparent_1px)] bg-size-[20px_20px]" />
      </div>

      <div className="relative flex w-full overflow-hidden">
        <UserSettingsSidebar
          totalSettings={settings.length}
          loading={loading}
        />

        <main className="flex-1 overflow-auto p-8">
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-[40px] font-bold tracking-tight text-foreground">
              Configuración
            </h2>
            <p className="text-foreground/50 text-lg font-medium mt-1">
              Gestiona las claves y valores de configuración del sistema.
            </p>

            <GlassContainer className="flex gap-2 mt-6 p-1 w-fit">
              {tabs.map((d, i) => (
                <button
                  key={d}
                  onClick={() => setActiveTab(i)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-semibold capitalize transition-all cursor-pointer ${activeTab === i
                      ? "bg-white text-accent shadow-md"
                      : "text-foreground/50 hover:text-foreground hover:bg-white/40"
                    }`}
                >
                  {d}
                </button>
              ))}
            </GlassContainer>
          </div>

          {isUserTab ? (
            <UserAccountPanel />
          ) : isMcpTab ? (
            <McpConfigPanel />
          ) : (
            <UserSettingsTable
              settings={filteredSettings}
              loading={loading}
              error={error}
              onGet={get}
              onSet={set}
            />
          )}
        </main>
      </div>
    </div>
  );
}
